import { Suspense, lazy } from "react";
import { Route, Navigate } from "react-router-dom";

import Loader from "./components/Loader";
import Layout from "./layout/Layout";
import { permissionService } from "./services/permissionService";
import { api } from "./utils/api";
import { renderWorkspaceAnalyticsRoutes } from "../admin-shared/analytics/renderAnalyticsRoutes";

const LoginSign = lazy(() => import("./pages/LoginSign"));
const KioskLandings = lazy(() => import("./pages/KioskLandings"));
const KioskLandingBuilder = lazy(() => import("./pages/KioskLandingBuilder"));
const KioskRegisterForms = lazy(() => import("./pages/KioskRegisterForms"));
const KioskRegisterFormBuilder = lazy(() => import("./pages/KioskRegisterFormBuilder"));
const SkinAnalysisSettings = lazy(() => import("./pages/SkinAnalysisSettings"));
const PageSetupWorkspace = lazy(() => import("./pages/PageSetupWorkspace"));
const FranchiseThemePacks = lazy(() => import("./pages/FranchiseThemePacks"));
const Servicelist = lazy(() => import("./pages/Servicelist"));
const ServiceManagement = lazy(() => import("./pages/ServiceManagement"));
const ServiceListReadOnly = lazy(() => import("./pages/ServiceListReadOnly"));
const WorkspaceProductCatalog = lazy(() => import("./pages/WorkspaceProductCatalog"));
const AddService = lazy(() => import("./pages/AddService"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const NaturalsKpis = lazy(() => import("./pages/EnterpriseKpis"));
const Complaints = lazy(() => import("./pages/Complaints"));
const HqCheckinAnalyticsPage = lazy(() => import("../admin-shared/HqCheckinAnalyticsPage"));
const UserList = lazy(() => import("./pages/UserList"));
const AdminContactMessages = lazy(() => import("./pages/AdminContactMessages"));
const HqAdmins = lazy(() => import("./pages/HqAdmins"));
const StoreLocationRequests = lazy(() => import("./pages/StoreLocationRequests"));
const GeofenceSettings = lazy(() => import("./pages/GeofenceSettings"));
const Regions = lazy(() => import("./pages/Regions"));
const SetPassword = lazy(() => import("./pages/SetPassword"));
const Verify = lazy(() => import("./pages/Verify"));
const NaturalsOverviewPage = lazy(() => import("../admin-shared/pages/EnterpriseOverviewPage"));
const OverviewSectionHubPage = lazy(() => import("../admin-shared/pages/OverviewSectionHubPage"));
const EngagementApp = lazy(() => import("../engagement/EngagementApp"));
const WhatsAppApp = lazy(() => import("../whatsapp/WhatsAppApp"));
const LicenseManagementPage = lazy(() => import("../admin-shared/LicenseManagementPage"));
const PlanManagementPage = lazy(() => import("../admin-shared/PlanManagementPage"));
const Invoices = lazy(() => import("./pages/Invoices"));
const InvoiceDetail = lazy(() => import("./pages/InvoiceDetail"));
const NaturalsFlowRelease = lazy(() => import("./pages/NaturalsFlowRelease"));
const RouteFallback = () => <Loader fullScreen />;
const withSuspense = (node) => <Suspense fallback={<RouteFallback />}>{node}</Suspense>;

const PermRoute = ({ children, check }) => {
  if (typeof check === "function" && !check()) {
    return <Navigate to="/nyafaz-ep/workspace/overview" replace />;
  }
  return children;
};

export function renderWorkspaceRoutes({ workspaceHome = false } = {}) {
  return (
    <>
      {workspaceHome ? <Route path="/" element={<Navigate to="/nyafaz-ep/workspace" replace />} /> : null}
      <Route path="/nyafaz-ep/workspace" element={<Navigate to="/nyafaz-ep/login" replace />} />
      <Route
        path="/nyafaz-ep/workspace/overview"
        element={
          <Layout>
            {withSuspense(<NaturalsOverviewPage portal="workspace" permissionService={permissionService} />)}
          </Layout>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/overview/:sectionId"
        element={
          <Layout>
            {withSuspense(
              <OverviewSectionHubPage portal="workspace" permissionService={permissionService} />,
            )}
          </Layout>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/engagement/*"
        element={
          <PermRoute check={() => permissionService.canAccessCustomerEngagement()}>
            <Layout>{withSuspense(<EngagementApp />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/engagement/"
        element={
          <PermRoute check={() => permissionService.canAccessCustomerEngagement()}>
            <Layout>{withSuspense(<EngagementApp />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/engagement"
        element={<Navigate to="/nyafaz-ep/workspace/engagement/" replace />}
      />
      <Route
        path="/nyafaz-ep/workspace/whatsapp/*"
        element={
          <PermRoute check={() => permissionService.canAccessWhatsApp()}>
            <Layout>{withSuspense(<WhatsAppApp />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/whatsapp"
        element={<Navigate to="/nyafaz-ep/workspace/whatsapp/accounts" replace />}
      />
      <Route
        path="/nyafaz-ep/workspace/catalog-setup"
        element={<Navigate to="/nyafaz-ep/workspace/overview/setup" replace />}
      />
      <Route path="/nyafaz-ep/workspace/nyafaz-hub" element={<Navigate to="/nyafaz-ep/workspace/overview" replace />} />
      <Route path="/nyafaz-ep/activate/set-password/:token" element={withSuspense(<SetPassword />)} />
      <Route path="/nyafaz-ep/activate/verify/:token" element={withSuspense(<Verify />)} />
      <Route
        path="/nyafaz-ep/workspace/admin-dashboard"
        element={<Layout>{withSuspense(<AdminDashboard />)}</Layout>}
      />
      <Route
        path="/nyafaz-ep/workspace/licenses"
        element={
          <PermRoute check={() => permissionService.canCreateFranchiseUsers()}>
            <Layout>
              {withSuspense(
                <LicenseManagementPage
                  api={api}
                  portalBase="/nyafaz-ep/workspace"
                  portalLabel="Workspace"
                />,
              )}
            </Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/plans"
        element={
          <PermRoute check={() => permissionService.canCreateFranchiseUsers()}>
            <Layout>
              {withSuspense(
                <PlanManagementPage api={api} licenseBase="/nyafaz-ep/licenses" />,
              )}
            </Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/naturals-kpis"
        element={
          <PermRoute check={() => permissionService.canViewStoreKpis()}>
            <Layout>{withSuspense(<NaturalsKpis />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/complaints"
        element={
          <PermRoute check={() => permissionService.canViewComplaints()}>
            <Layout>{withSuspense(<Complaints />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/check-in-analytics"
        element={
          <PermRoute
            check={() =>
              permissionService.canViewStoreKpis() &&
              permissionService.canAccessGuestOpsAnalytics()
            }
          >
            <Layout>
              {withSuspense(
                <HqCheckinAnalyticsPage
                  api={api}
                  portalBase="/nyafaz-ep/workspace"
                  portalLabel="Workspace"
                  Loader={Loader}
                />,
              )}
            </Layout>
          </PermRoute>
        }
      />
      {renderWorkspaceAnalyticsRoutes({
        withSuspense,
        Layout,
        PermRoute,
        canViewSkin: () => permissionService.canAccessSkinAnalytics(),
        canViewHair: () => permissionService.canAccessHairAnalytics(),
        canViewFlow: () =>
          permissionService.canViewStoreKpis() && permissionService.canAccessFlowAnalytics(),
        canViewGuestOps: () =>
          permissionService.canViewStoreKpis() && permissionService.canAccessGuestOpsAnalytics(),
        hqApi: api,
      })}
      <Route
        path="/nyafaz-ep/workspace/kiosk-landings"
        element={
          <PermRoute
            check={() =>
              permissionService.canManageKioskLandings() &&
              permissionService.canAccessKioskSkinStack()
            }
          >
            <Layout>{withSuspense(<KioskLandings />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/kiosk-landings/:pageKey"
        element={
          <PermRoute
            check={() =>
              permissionService.canManageKioskLandings() &&
              permissionService.canAccessKioskSkinStack()
            }
          >
            <Layout>{withSuspense(<KioskLandingBuilder />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/kiosk-register-forms"
        element={
          <PermRoute
            check={() =>
              permissionService.canManageKioskLandings() &&
              permissionService.canAccessKioskSkinStack()
            }
          >
            <Layout>{withSuspense(<KioskRegisterForms />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/kiosk-register-forms/:pageKey"
        element={
          <PermRoute
            check={() =>
              permissionService.canManageKioskLandings() &&
              permissionService.canAccessKioskSkinStack()
            }
          >
            <Layout>{withSuspense(<KioskRegisterFormBuilder />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/kiosk-terms"
        element={<Navigate to="/nyafaz-ep/workspace/overview" replace />}
      />
      <Route
        path="/nyafaz-ep/workspace/kiosk-terms/:pageKey"
        element={<Navigate to="/nyafaz-ep/workspace/overview" replace />}
      />
      <Route
        path="/nyafaz-ep/workspace/page-setup"
        element={
          <PermRoute check={() => permissionService.canEditSetFlow()}>
            <Layout>{withSuspense(<PageSetupWorkspace />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/franchise-themes"
        element={
          <PermRoute
            check={() =>
              permissionService.canEditSetFlow() || permissionService.canManageKioskLandings()
            }
          >
            <Layout>{withSuspense(<FranchiseThemePacks />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/skin-analysis-settings"
        element={
          <PermRoute check={() => permissionService.canAccessSkinSettings()}>
            <Layout>{withSuspense(<SkinAnalysisSettings />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/servicelist"
        element={
          <PermRoute check={() => permissionService.canAccessServiceCatalogAdmin()}>
            <Layout>{withSuspense(<Servicelist />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/service-management"
        element={
          <PermRoute check={() => permissionService.canAccessServiceCatalogAdmin()}>
            <Layout>{withSuspense(<ServiceManagement />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/naturals-flow-release"
        element={<Layout>{withSuspense(<NaturalsFlowRelease />)}</Layout>}
      />
      <Route
        path="/nyafaz-ep/workspace/service-list-readonly"
        element={
          <PermRoute check={() => permissionService.canViewOrgServicesReadonly()}>
            <Layout>{withSuspense(<ServiceListReadOnly />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/product-list"
        element={
          <PermRoute check={() => permissionService.canAccessProductCatalogAdmin()}>
            <Layout>{withSuspense(<WorkspaceProductCatalog />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/productlist"
        element={<Navigate to="/nyafaz-ep/workspace/product-list" replace />}
      />
      <Route
        path="/nyafaz-ep/workspace/add-service"
        element={
          <PermRoute check={() => permissionService.canAccessServiceCatalogAdmin()}>
            <Layout>{withSuspense(<AddService />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/edit-service"
        element={
          <PermRoute check={() => permissionService.canAccessServiceCatalogAdmin()}>
            <Layout>{withSuspense(<AddService />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/view-service"
        element={
          <PermRoute check={() => permissionService.canAccessServiceCatalogAdmin()}>
            <Layout>{withSuspense(<AddService />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/franchise-users"
        element={
          <PermRoute check={() => permissionService.canCreateFranchiseUsers()}>
            <Layout>{withSuspense(<UserList />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/regions"
        element={
          <PermRoute
            check={() =>
              permissionService.isSuperAdmin() || permissionService.canCreateFranchiseUsers()
            }
          >
            <Layout>{withSuspense(<Regions />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/admin-contact-messages"
        element={
          <PermRoute check={() => permissionService.canViewContactMessages()}>
            <Layout>{withSuspense(<AdminContactMessages />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/hq-admins"
        element={
          <PermRoute check={() => permissionService.canManageHqAdmins()}>
            <Layout>{withSuspense(<HqAdmins />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/store-location-requests"
        element={
          <PermRoute check={() => permissionService.canViewStoreKpis()}>
            <Layout>{withSuspense(<StoreLocationRequests />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/geofence-settings"
        element={
          <PermRoute check={() => permissionService.isAdmin()}>
            <Layout>{withSuspense(<GeofenceSettings />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/edit-profile"
        element={
          <PermRoute
            check={() =>
              permissionService.getRole() === "admin" && !permissionService.isFranchise()
            }
          >
            <Layout>{withSuspense(<EditProfile />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/invoices"
        element={
          <PermRoute check={() => permissionService.canCreateFranchiseUsers()}>
            <Layout>{withSuspense(<Invoices />)}</Layout>
          </PermRoute>
        }
      />
      <Route
        path="/nyafaz-ep/workspace/invoice/:id"
        element={
          <PermRoute check={() => permissionService.canCreateFranchiseUsers()}>
            <Layout>{withSuspense(<InvoiceDetail />)}</Layout>
          </PermRoute>
        }
      />
    </>
  );
}
